<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import {
  DModal,
  InfoCircleIcon,
  DTable,
  DTextField,
  DLoading,
  DTextAreaField,
  XIcon,
  Loading02Icon,
  DToggleField
} from 'front-library'
import { useI18n } from 'vue-i18n'
import { bool, object, string } from 'yup'
import { useField, useForm } from 'vee-validate'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useNotificationsStore } from '@/store/notifications'
import {
  createEmployeeCodeChangeRequest,
  EmployeeCodeChangeRequest,
  getCodeChanges
} from '@/app/core/services/employees'

const { t } = useI18n()

const props = defineProps<{
  employeeForModify: string
  hasRequests: boolean
  showModal: boolean
}>()

const emit = defineEmits(['get:employees', 'close:modal', 'modify:hasRequests'])

const emitEmployees = async () => {
  await emit('get:employees')
}

const emitModifyHasRequests = () => {
  emit('modify:hasRequests')
}

const closeModal = () => {
  emit('close:modal')
}

const closeAndReset = () => {
  closeModal()
  resetCodeChangeRequestForm()
}

const columns = ref([
  {
    name: 'newCode',
    title: t('employeeCodeChangeRequests.list.newCode'),
    table: 'newCode'
  },
  {
    name: 'oldCode',
    title: t('employeeCodeChangeRequests.list.oldCode'),
    table: 'oldCode'
  },
  {
    name: 'requestStatusName',
    title: t('employeeCodeChangeRequests.list.requestStatusName'),
    table: 'requestStatusName'
  },
  {
    name: 'observation',
    title: t('employeeCodeChangeRequests.list.observation'),
    table: 'observation'
  },
  {
    name: 'includeHistoricalUpdate',
    title: t('employeeCodeChangeRequests.list.includeHistoricalUpdate'),
    table: 'includeHistoricalUpdate'
  },
  {
    name: 'userName',
    title: t('employeeCodeChangeRequests.list.userName'),
    table: 'userName'
  },
  {
    name: 'errorMessage',
    title: t('employeeCodeChangeRequests.list.errors'),
    table: 'errorMessage'
  },
  {
    name: 'createdAt',
    title: t('employeeCodeChangeRequests.list.createdAt'),
    table: 'createdAt'
  },
  {
    name: 'updatedAt',
    title: t('employeeCodeChangeRequests.list.updatedAt'),
    table: 'updatedAt'
  }
])

const rows = ref<EmployeeCodeChangeRequest[]>([])
const perPage = ref(1)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

const notifications = useNotificationsStore()
const loading = ref(false)
const messageLoading = ref('')

const tableCodeChange = ref(null)

const dataCodeChangeRequest = ref({
  newCode: '',
  observation: '',
  includeHistoricalUpdate: false
})

const validationSchemaCodeChangeRequest = object().shape({
  newCode: string()
    .required(t('form.valid.required'))
    .min(1, t('form.valid.minLength', [1]))
    .max(50, t('form.valid.maxLength', [50]))
    .matches(
      /^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/,
      t('form.valid.alphanumeric')
    ),
  observation: string()
    .required(t('form.valid.required'))
    .min(20, t('form.valid.minLength', [20]))
    .max(100, t('form.valid.maxLength', [100])),
  includeHistoricalUpdate: bool()
    .required(t('form.valid.required'))
    .default(false)
})

const { errors, setErrors, resetForm } = useForm({
  initialValues: dataCodeChangeRequest.value,
  validationSchema: validationSchemaCodeChangeRequest
})

const resetCodeChangeRequestForm = () => {
  loading.value = false
  resetForm()
  setErrors({})
}

const { value: observation } = useField<string>(ref('observation'))
const { value: includeHistoricalUpdate } = useField<boolean>(
  ref('includeHistoricalUpdate')
)
const { value: newCode } = useField<string>(ref('newCode'))

const createCodeChangeRequest = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    await createEmployeeCodeChangeRequest(
      props.employeeForModify,
      newCode.value,
      observation.value,
      includeHistoricalUpdate.value
    )
    notifications.addNotification({
      title: t('notification.created'),
      text: t('employeeCodeChangeRequests.notification', [
        t('notification.wasCreated')
      ]),
      type: 'success',
      time: 10000
    })

    loading.value = false
    await emitEmployees()
    closeModal()
    resetCodeChangeRequestForm()
  } catch (error: any) {
    notificationError(error, 'employees', t, notifications, loading)
  }
}

const codeChanges = async () => {
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getCodeChanges(
      props.employeeForModify,
      offset.value,
      perPage.value,
      sortBy.value
    )
    rows.value = data[0].results
    rows.value.forEach(_ => {
      _.errorMessage = _.errors
        .map(error =>
          error.code
            ? t(`error.messages.${error.code}`, {
                process: error?.parameters?.processName || '',
                dateEnd: error?.parameters?.dateEnd || ''
              })
            : error.message
        )
        .map(msg => `- ${msg}`)
        .join('\n\n')
    })
    totalRows.value = data[0].totalItems
  } catch (error: any) {
    notificationError(error, 'employees', t, notifications, loading)
  }
}

onMounted(async () => {
  setInterval(async () => {
    if (props.showModal) {
      await codeChanges()
    }
  }, 5000)
})
</script>
<template>
  <DModal
    :open="showModal"
    v-if="!hasRequests"
    :isFooter="false"
    :size="'w-4/5 lg:w-1/3 2xl:w-1/4'"
    class="px-5"
    @closeModal="closeAndReset">
    <template v-slot:form>
      <DLoading v-show="loading" :message="messageLoading" />

      <div class="absolute top-4 right-4 cursor-pointer" @click="closeAndReset">
        <XIcon />
      </div>

      <div class="flex flex-col items-center gap-10">
        <section
          class="flex items-center justify-center rounded-full sm:mx-0 h-14 w-14 bg-red-100">
          <InfoCircleIcon class="h-8 w-8 text-red-500" />
        </section>
        <section class="px-5 text-center text-gray-400 text-sm">
          <h1
            class="text-black text-xl"
            v-html="t('employeeCodeChangeRequests.title')" />
          <p
            class="my-5"
            v-html="t('employeeCodeChangeRequests.confirmationMessage')" />
          <DTextField
            class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
            :error="errors.newCode"
            :placeholder="t('employees.fields.code.label')"
            :required="true"
            v-model="newCode" />
          <DTextAreaField
            v-model="observation"
            :placeholder="t('employeeCodeChangeRequests.observation')"
            :error="errors.observation"
            :resize="false"
            :rows="7" />

          <DToggleField
            v-model="includeHistoricalUpdate"
            :label="t('employeeCodeChangeRequests.includeHistoricalUpdate')"
            :error="errors.includeHistoricalUpdate"
            :required="true"
            class="mt-4" />
        </section>
        <section class="flex gap-3">
          <button
            v-if="rows.length === 0"
            @click="closeAndReset"
            class="hover:bg-gray-600 hover:text-white focus:ring-gray-200 inline-flex text-sm justify-center items-center py-1.5 px-10 border border-gray-300 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
            v-html="t('form.button.cancel')" />

          <button
            v-if="rows.length > 0"
            @click="emitModifyHasRequests"
            class="hover:bg-gray-600 hover:text-white focus:ring-gray-200 inline-flex text-sm justify-center items-center py-1.5 px-10 border border-gray-300 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
            v-html="t('form.button.back')" />
          <button
            @click="createCodeChangeRequest"
            class="bg-blue-600 text-white hover:bg-blue-700 inline-flex text-sm justify-center items-center py-1.5 px-10 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
            v-html="t('form.button.confirm')" />
        </section>
      </div>
    </template>
  </DModal>

  <DModal
    :open="showModal"
    v-if="hasRequests"
    :isFooter="false"
    :size="'w-5/6'"
    class="px-5 "
    @closeModal="closeAndReset">
    <template v-slot:form>
      <div class="flex flex-col items-center gap-2 w-100">
        <div
          class="absolute top-4 right-4 cursor-pointer"
          @click="closeAndReset">
          <XIcon />
        </div>

        <section
          class="px-5 text-center text-gray-400 text-sm w-full flex items-center flex-col pb-5">
          <section
            class="flex items-center justify-center rounded-full sm:mx-0 h-14 w-14 bg-blue-100">
            <InfoCircleIcon class="h-8 w-8 text-blue-500" />
          </section>
          <h1
            class="text-black text-2xl pb-2"
            v-html="t('employeeCodeChangeRequests.table')" />
        </section>

        <hr class="w-full pb-10" />
        <DTable
          ref="tableCodeChange"
          :columns="columns"
          :rows="rows"
          :total-rows="totalRows"
          class="max-h-[calc(60vh-200px)] w-[60vw] md:w-[70vw] lg:w-[75vw]"
          :select-options="false"
          :text-no-data="t('table.noData')"
          :text-per-page="t('table.rowsPerPage')"
          @perPage=";(perPage = $event), codeChanges()"
          @offset="offset = $event"
          @sort=";(sortBy = $event), codeChanges()">
          <template #requestStatusName="data: any">
            <div
              class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
              :class="{
                'bg-blue-100 text-blue-600':
                  data.requestStatusName === 'Pendiente',
                'bg-green-100 text-green-600':
                  data.requestStatusName === 'Aprobado',
                'bg-red-100 text-red-600':
                  data.requestStatusName === 'Rechazado'
              }">
              <div
                class="mr-2 w-1.5 h-1.5 rounded-full"
                :class="{
                  'bg-blue-600': data.requestStatusName === 'Pendiente',
                  'bg-green-600': data.requestStatusName === 'Aprobado',
                  'bg-red-600': data.requestStatusName === 'Rechazado'
                }"></div>
              {{ data.requestStatusName }}
            </div>
          </template>
          <template #errorMessage="data: any">
            <div class="w-[250px] whitespace-pre-line flex">
              <Loading02Icon
                v-if="
                  data.requestStatusName == 'Pendiente' && !data.errorMessage
                "
                class="h-6 w-6 mx-2 cursor-pointer text-blue-600 animate-spin" />
              {{ data.errorMessage || 'Sin errores registrados' }}
            </div>
          </template>
          <template #actions="data: any"> </template>
        </DTable>

        <section class="flex gap-3 py-5">
          <button
            @click="closeAndReset"
            class="hover:bg-gray-600 hover:text-white focus:ring-gray-200 inline-flex text-sm justify-center items-center py-1.5 px-10 border border-gray-300 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
            v-html="t('form.button.cancel')" />
          <button
            @click="emitModifyHasRequests(), resetCodeChangeRequestForm()"
            class="bg-blue-600 text-white hover:bg-blue-700 inline-flex text-sm justify-center items-center py-1.5 px-10 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
            v-html="t('form.button.createRequest')" />
        </section>
      </div>
    </template>
  </DModal>
</template>
<style scoped>
.d-modal {
  max-width: 600px;
}
</style>
